/**
 * GamificationRenderer — Universal renderer for any GamificationType
 *
 * Entry point for rendering a gamification model. Fetches the model,
 * subscribes to WebSocket updates, wraps in Chrome, and delegates
 * to the type-specific renderer from the registry.
 *
 * Usage:
 *   <GamificationRenderer gamificationModelId="abc123" />
 *   <GamificationRenderer gamificationModelId="abc123" displayMode="compact" />
 */
import {Suspense} from "react";
import {useGetGamificationModel} from "@/hooks/gamification/useGetGamificationModel";
import {useGamificationSocket} from "@/hooks/gamification/useGamificationSocket";
import {getRenderer, hasRenderer, GamificationDisplayMode} from "./rendererRegistry";
import {GamificationChrome} from "./GamificationChrome";
import "./GamificationRenderer.scss";

interface GamificationRendererProps {
    gamificationModelId: string;
    displayMode?: GamificationDisplayMode;
    sectionTitle?: string;
}

export const GamificationRenderer = ({
    gamificationModelId,
    displayMode = "full",
    sectionTitle,
}: GamificationRendererProps) => {
    const {data: model, isLoading, error} = useGetGamificationModel(gamificationModelId);
    useGamificationSocket(gamificationModelId);

    if (isLoading) {
        return (
            <div className="gamification-renderer gamification-renderer--loading">
                <div className="gamification-renderer__skeleton" />
            </div>
        );
    }

    if (error || !model) {
        return null;
    }

    const TypeRenderer = getRenderer(model.type);

    return (
        <div className={`gamification-renderer gamification-renderer--${displayMode}`}>
            <GamificationChrome model={model} displayMode={displayMode} sectionTitle={sectionTitle}>
                {TypeRenderer
                    ? <Suspense fallback={<div className="gamification-renderer__skeleton" />}>
                        <TypeRenderer model={model} displayMode={displayMode} />
                    </Suspense>
                    : <div className="gamification-renderer__unimplemented">
                        {hasRenderer(model.type) ? "Loading..." : `${model.type} renderer not yet available`}
                    </div>
                }
            </GamificationChrome>
        </div>
    );
};
